/**
 * Umbrella co-branding on child dashboards (NOS-171): "Sponsored by [X]" for
 * active fiscal children (logo + name), smaller text-only treatment for
 * affiliated. Read-only informational — never rendered in the builder or on
 * the public site.
 */
import { useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"

interface SponsorBrand {
  name: string
  logoUrl: string | null
  mode: "fiscal" | "affiliated"
}

export function SponsorCoBrand({ orgId }: { orgId: string }) {
  const [brand, setBrand] = useState<SponsorBrand | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      const { data: link } = await supabase
        .from("sponsorships")
        .select(
          "mode, status, sponsor:organizations!sponsorships_sponsor_org_id_fkey(name, legal_name, logo_url)"
        )
        .eq("child_org_id", orgId)
        .in("status", ["active", "transferring"])
        .maybeSingle()
      if (cancelled) return
      const sponsor = link?.sponsor as unknown as {
        name: string
        legal_name: string | null
        logo_url: string | null
      } | null
      if (!link || !sponsor) {
        setBrand(null)
        return
      }
      setBrand({
        name: sponsor.legal_name?.trim() || sponsor.name,
        logoUrl: sponsor.logo_url,
        mode: link.mode as "fiscal" | "affiliated",
      })
    }
    load()
    return () => {
      cancelled = true
    }
  }, [orgId])

  if (!brand) return null

  if (brand.mode === "affiliated") {
    return (
      <span className="text-xs text-gray-400">Affiliated with {brand.name}</span>
    )
  }

  return (
    <span className="flex items-center gap-1.5 text-xs text-gray-500">
      {brand.logoUrl && (
        <img src={brand.logoUrl} alt="" className="h-4 w-4 rounded object-contain" />
      )}
      Sponsored by {brand.name}
    </span>
  )
}
